import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SignalIndicator } from './signal-indicator';
import type { SignalStrength } from '../../shared';

interface SignalCardProps {
  title: string;
  icon: React.ReactNode;
  strength: SignalStrength;
  analysis: string;
  details?: React.ReactNode;
}

export function SignalCard({ title, icon, strength, analysis, details }: SignalCardProps) {
  const { t } = useTranslation();

  const strengthTextMap = {
    1: t('signal.strongBearish'),
    2: t('signal.bearish'),
    3: t('signal.neutral'),
    4: t('signal.bullish'),
    5: t('signal.strongBullish')
  };

  const strengthText = strengthTextMap[strength];

  const colorClasses = {
    1: 'border-red-200 bg-red-50',
    2: 'border-orange-200 bg-orange-50',
    3: 'border-yellow-200 bg-yellow-50',
    4: 'border-lime-200 bg-lime-50',
    5: 'border-green-200 bg-green-50',
  };

  return (
    <Card className={colorClasses[strength]}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
          <span className="text-sm font-semibold text-slate-700">{strengthText}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SignalIndicator strength={strength} size="md" />
        <p className="text-sm text-slate-700">{analysis}</p>
        {details && <div className="mt-4 pt-4 border-t border-slate-200">{details}</div>}
      </CardContent>
    </Card>
  );
}
